import { DateOnly } from '@hantera/design-system'
import Decimal from 'decimal.js'

export type ParamKind =
  | { kind: 'text' }
  | { kind: 'number' }
  | { kind: 'boolean' }
  | { kind: 'instant' }
  | { kind: 'date' }
  | { kind: 'list'; itemType: string; itemKind: ParamKind }
  | { kind: 'json' }

const PRIMITIVE_KINDS: Record<string, ParamKind> = {
  text: { kind: 'text' },
  number: { kind: 'number' },
  boolean: { kind: 'boolean' },
  bool: { kind: 'boolean' },
  instant: { kind: 'instant' },
  date: { kind: 'date' },
}

export function parseParamType(type: string | undefined): ParamKind {
  if (!type) return { kind: 'json' }

  const trimmed = type.trim()
  const lower = trimmed.toLowerCase()

  if (PRIMITIVE_KINDS[lower]) return PRIMITIVE_KINDS[lower]

  if (trimmed.startsWith('[') && trimmed.endsWith(']') && isBalancedBrackets(trimmed)) {
    const inner = trimmed.slice(1, -1).trim()
    return {
      kind: 'list',
      itemType: inner,
      itemKind: parseParamType(inner),
    }
  }

  return { kind: 'json' }
}

function isBalancedBrackets(value: string): boolean {
  let depth = 0
  for (let i = 0; i < value.length; i++) {
    const c = value[i]
    if (c === '[') depth++
    else if (c === ']') {
      depth--
      if (depth === 0 && i !== value.length - 1) return false
      if (depth < 0) return false
    }
  }
  return depth === 0
}

export function defaultForKind(kind: ParamKind): unknown {
  switch (kind.kind) {
    case 'text':
      return ''
    case 'number':
      return undefined
    case 'boolean':
      return false
    case 'instant':
    case 'date':
      return undefined
    case 'list':
      return []
    case 'json':
      return ''
  }
}

export function coerceDefault(kind: ParamKind, raw: unknown): unknown {
  if (raw === null || raw === undefined) return defaultForKind(kind)

  switch (kind.kind) {
    case 'text':
      return typeof raw === 'string' ? raw : String(raw)

    case 'number':
      return coerceNumber(raw)

    case 'boolean':
      if (typeof raw === 'boolean') return raw
      if (typeof raw === 'string') {
        const lower = raw.toLowerCase()
        if (lower === 'true') return true
        if (lower === 'false') return false
      }
      return false

    case 'instant':
      return coerceInstant(raw)

    case 'date':
      return coerceDate(raw)

    case 'list': {
      const list = coerceListSource(raw)
      if (!list) return []
      return list.map((item) => coerceDefault(kind.itemKind, item))
    }

    case 'json':
      return formatJsonDraft(raw)
  }
}

function coerceNumber(raw: unknown): Decimal | undefined {
  if (raw instanceof Decimal) return raw
  if (typeof raw === 'number' && !isNaN(raw)) return new Decimal(raw)
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      return new Decimal(raw)
    } catch {
      return undefined
    }
  }
  return undefined
}

function coerceInstant(raw: unknown): Date | undefined {
  if (raw instanceof Date) return isNaN(raw.getTime()) ? undefined : raw
  if (typeof raw === 'number') {
    const d = new Date(raw)
    return isNaN(d.getTime()) ? undefined : d
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const d = new Date(raw)
    return isNaN(d.getTime()) ? undefined : d
  }
  return undefined
}

function coerceDate(raw: unknown): DateOnly | undefined {
  if (raw instanceof DateOnly) return raw
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? undefined : DateOnly.fromDate(raw)
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const trimmed = raw.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      try {
        return DateOnly.parse(trimmed)
      } catch {
        return undefined
      }
    }
    const d = new Date(trimmed)
    return isNaN(d.getTime()) ? undefined : DateOnly.fromDate(d)
  }
  return undefined
}

function coerceListSource(raw: unknown): unknown[] | undefined {
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return []
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // fall through
    }
  }
  return undefined
}

function formatJsonDraft(raw: unknown): string {
  if (typeof raw === 'string') return raw
  try {
    return JSON.stringify(raw, jsonReplacer, 2)
  } catch {
    return ''
  }
}

function jsonReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Decimal) return value.toNumber()
  if (value instanceof DateOnly) return value.toString()
  if (value instanceof Date) return value.toISOString()
  return value
}

export function isEmptyValue(kind: ParamKind, value: unknown): boolean {
  switch (kind.kind) {
    case 'text':
      return typeof value !== 'string' || value === ''
    case 'number':
      return value === undefined || value === null
    case 'boolean':
      return false
    case 'instant':
    case 'date':
      return value === undefined || value === null
    case 'list':
      return !Array.isArray(value) || value.length === 0
    case 'json':
      return typeof value !== 'string' || value.trim() === ''
  }
}

export function serializeValue(kind: ParamKind, value: unknown): unknown {
  switch (kind.kind) {
    case 'text':
      return value ?? ''

    case 'number': {
      if (value instanceof Decimal) return value.toNumber()
      if (typeof value === 'number') return value
      // Decimal.js may be duplicated across workspaces, so a foreign Decimal
      // can fail the instanceof check. Accept any object exposing toNumber().
      if (value && typeof (value as { toNumber?: unknown }).toNumber === 'function') {
        return (value as { toNumber: () => number }).toNumber()
      }
      throw new Error('Invalid number value')
    }

    case 'boolean':
      return value === true

    case 'instant': {
      if (value instanceof Date) return value.toISOString()
      throw new Error('Invalid instant value')
    }

    case 'date': {
      if (value instanceof DateOnly) return value.toString()
      throw new Error('Invalid date value')
    }

    case 'list': {
      if (!Array.isArray(value)) return []
      return value
        .filter((item) => !isEmptyValue(kind.itemKind, item))
        .map((item) => serializeValue(kind.itemKind, item))
    }

    case 'json': {
      if (typeof value !== 'string' || value.trim() === '') {
        throw new Error('Empty JSON value')
      }
      try {
        return JSON.parse(value)
      } catch {
        throw new Error('Invalid JSON')
      }
    }
  }
}
